import { defineMessages, useIntl } from 'react-intl';
import React, { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';

import AdvancedHotkey from './component';
import { orderGridOpenMarkShippedModal } from '../../../store/orders/grid/orders.grid.header.actions';
import { BASE_MAS_PAGE } from '../../../orders/mark-as-shipped/constants';
import { ON_HOLD, AWAITING_PAYMENT, AWAITING_SHIPMENT } from '../../../services/api/order-status-from-view-service';
import Global from '../../../store/global-selectors';
import { getActualSalesOrderIds } from '../../../store/orders/orders.helpers';
import {
  ADVANCED_SEARCH_ID,
  CANCELLED_ID,
  PENDING_FULFILLMENT_ID,
  REJECTED_FULFILLMENT_ID,
  SHIPPED_ID,
} from '../../../pages/orders-page/orders-page-view-constants';
import { useTopLevelOrdersViewId } from '../../../pages/orders-page/orders-left-nav/orders-left-nav-context';

export const messages = defineMessages({
  name: {
    description: 'ordersPage.MarkAsShippedHotkey',
    defaultMessage: 'Mark As Shipped',
    id: 'cNN2f5',
  },
});

export const hotkey = ['m', 's'];

const viewsWithoutMarkAsShipped = [PENDING_FULFILLMENT_ID, REJECTED_FULFILLMENT_ID, SHIPPED_ID, CANCELLED_ID];
const advancedSearchOtherActionsAllowedStatus = [ON_HOLD, AWAITING_SHIPMENT, AWAITING_PAYMENT];

const isDisabled = ({ selectedOrdersCount, orderGridViewName, hasDisablingAdvancedSearchStatus }) => {
  if (orderGridViewName === ADVANCED_SEARCH_ID && hasDisablingAdvancedSearchStatus) {
    return true;
  }
  return selectedOrdersCount && viewsWithoutMarkAsShipped.includes(orderGridViewName);
};

const MarkAsShippedHotkey = ({ children }) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const selectedOrdersCount = useSelector(Global.selectSelectedOrdersCount) || 0;
  const orderGridViewName = useTopLevelOrdersViewId();

  const selectedOrdersIds = useSelector(Global.selectSelectedOrdersIds) || [];
  const actualSelectedOrdersIds = useSelector((state) => getActualSalesOrderIds(state, selectedOrdersIds));
  const fulfillmentPlans =
    useSelector((state) =>
      Global.selectEntitiesFulfillmentPlansBySalesOrderIds(state, {
        salesOrderIds: actualSelectedOrdersIds,
      }),
    ) || [];
  const fulfillmentPlanStatuses = new Set(fulfillmentPlans.map((plan) => plan.orderGridStatus));
  const hasDisablingAdvancedSearchStatus = [...fulfillmentPlanStatuses].some(
    (stat) => !advancedSearchOtherActionsAllowedStatus.includes(stat),
  );

  const handler = useCallback(() => {
    if (
      !isDisabled({
        orderGridViewName,
        selectedOrdersCount,
        hasDisablingAdvancedSearchStatus,
      })
    ) {
      dispatch(orderGridOpenMarkShippedModal(BASE_MAS_PAGE));
    }
  }, [dispatch, selectedOrdersCount, orderGridViewName, hasDisablingAdvancedSearchStatus]);

  const hotkeyMetadata = useMemo(
    () => ({
      displayHotkeyStrings: ['ms'],
      hotkey,
      handler,
      name: intl.formatMessage(messages.name),
    }),
    [intl, handler],
  );

  return <AdvancedHotkey hotkey={hotkeyMetadata}>{children}</AdvancedHotkey>;
};

MarkAsShippedHotkey.propTypes = {
  children: PropTypes.node.isRequired,
};

export default MarkAsShippedHotkey;
